import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api, imgUrl } from "@/lib/api";
import ServicesMarquee from "@/components/ServicesMarquee";
import BrandMarquee from "@/components/BrandMarquee";
import ProjectsGridEditorial from "@/components/ProjectsGridEditorial";

const BRANDS = [
  "Jaipur Watch Company", "Pashma Global", "Exotic India Art", "HumbleTree",
  "Materialé Homes", "Karm 108", "BansuribyPoonam", "The Urban Thing",
  "Deadkind", "Sort Marketing", "Ok Boomer", "Empty Set",
];

const SERVICES_LIST = [
  "Branding & visual identity",
  "Logo design",
  "Social media (monthly feed packages)",
  "Websites & Shopify stores",
  "Packaging & publication design",
  "UI/UX",
  "Shoots, video editing & 3D",
  "AI video & ad creatives",
  "& much more",
];

function SectionNum({ n, title, id }) {
  return (
    <div id={id} className="scroll-mt-24">
      <div className="font-mono uppercase text-[12px] tracking-[0.12em] text-muted2 mb-4">[ {n} ]</div>
      <h2 className="font-display uppercase font-[800] tracking-[-0.02em] leading-[0.9] text-4xl sm:text-5xl lg:text-6xl text-black">{title}</h2>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative">
      <div className="pt-[18vh] sm:pt-[26vh] md:pt-[32vh]" />
      <ServicesMarquee />
      <div className="relative mt-5 md:mt-7 px-2 md:px-4">
        <motion.div style={{ y }} className="select-none">
          <div
            className="font-display uppercase font-[900] leading-[0.95] tracking-[-0.03em] text-center whitespace-nowrap pb-[0.08em]"
            style={{ color: "#D1480D", fontSize: "min(37vw, 42vh)" }}
          >
            QISSA
          </div>
        </motion.div>
        <div className="hero-fade" />
      </div>
      <div className="px-4 md:px-6 mt-3 md:mt-4 text-center">
        <span className="font-mono uppercase text-[14px] tracking-[0.12em] font-medium" style={{ color: "#122A4E" }}>Creative studio</span>
      </div>
      <div className="mt-5 md:mt-7 border-y border-[#E5E5E5] py-1">
        <BrandMarquee />
      </div>
    </section>
  );
}

export default function Home() {
  const [content, setContent] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/content"), api.get("/projects")]).then(([c, p]) => {
      setContent(c.data);
      setProjects(p.data);
    });
  }, []);

  // smooth-scroll to hash after content loads
  useEffect(() => {
    if (!content) return;
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else if (el) el.scrollIntoView();
    }, 300);
    return () => clearTimeout(t);
  }, [content]);

  const featured = projects.filter((p) => p.featured);
  const shown = featured.length ? featured : projects;
  const c = content || {};

  return (
    <div className="bg-bg text-black">
      <Hero />

      {/* WORK GRID — selected projects */}
      <section id="work" className="scroll-mt-24 px-4 md:px-6 pt-20 md:pt-28">
        <div className="mb-14 md:mb-20">
          <span className="font-mono uppercase text-[12px] tracking-[0.12em] text-black">[ Selected Projects ]</span>
        </div>
        <ProjectsGridEditorial projects={shown} />
      </section>

      {/* STATEMENT BAND */}
      <section className="px-4 md:px-6 pt-40 md:pt-56">
        <h2 className="font-display uppercase font-[800] tracking-[-0.02em] leading-[1] text-3xl sm:text-4xl lg:text-5xl text-black max-w-[90%] md:max-w-[72%]">
          We build brands, social and websites end to end. From story to strategy to screen.
        </h2>
        <p className="font-serif text-lg md:text-xl text-black mt-8 max-w-[640px]">
          Ready to tell your story properly? Write to us at{" "}
          <a href="mailto:info@qissastudio.co" className="underline underline-offset-4">info@qissastudio.co</a>
        </p>
      </section>

      {/* [01] WHAT WE DO */}
      <section className="px-4 md:px-6 pt-40 md:pt-56">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionNum n="01" title="What we do" id="services" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ul className="font-serif text-xl sm:text-2xl leading-[1.7] text-black">
              {SERVICES_LIST.map((s) => <li key={s}>{s}</li>)}
            </ul>
            <Link to="/connect" data-testid="link-services" className="inline-block mt-8 font-mono uppercase text-[12px] tracking-[0.12em] hover:underline underline-offset-4">[ Services ]</Link>
          </div>
        </div>
      </section>

      {/* [02] WHO WE ARE (teaser -> /about) */}
      <section className="px-4 md:px-6 pt-40 md:pt-56">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionNum n="02" title="Who we are" id="about" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="font-serif text-lg md:text-xl leading-[1.7] text-black max-w-[640px]">
              Qissa means story. We're a small creative studio of designers and strategists in New Delhi, building brands worth playing on repeat. Boutique by choice. Global by accident. Chai-powered by necessity.
            </p>
            <Link to="/about" data-testid="link-about" className="inline-block mt-8 font-mono uppercase text-[12px] tracking-[0.12em] hover:underline underline-offset-4">[ About ]</Link>
          </div>
        </div>
      </section>

      {/* [03] THE FOUNDER */}
      <section className="px-4 md:px-6 pt-40 md:pt-56">
        <SectionNum n="03" title="The founder" id="founder" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-12 items-start">
          <div className="md:col-span-5">
            {c.founder_image && (
              <div className="aspect-[4/5] overflow-hidden bg-surface">
                <img src={imgUrl(c.founder_image, 800)} alt={c.founder_name || "Founder"} loading="lazy" className="w-full h-full object-cover grayscale" />
              </div>
            )}
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <h3 className="font-display uppercase font-[900] tracking-[-0.02em] leading-[0.9] text-5xl sm:text-6xl text-black">{c.founder_name || "Tanya Goel"}</h3>
            <p className="font-mono uppercase text-[13px] tracking-[0.1em] text-muted2 mt-4">{c.founder_role || "Founder & Creative Director"}</p>
            <p className="font-serif text-lg leading-[1.7] text-black mt-8 max-w-[560px]">
              {c.about_founder_bio || c.founder_body || "Tanya founded Qissa to build brands with real character — stories told through design, motion and strategy. She leads every project hands-on, from the first sketch to the final screen."}
            </p>
          </div>
        </div>
      </section>

      {/* CLIENTS & BRANDS */}
      <section className="px-4 md:px-6 pt-40 md:pt-56">
        <div className="font-mono uppercase text-[12px] tracking-[0.12em] text-muted2 mb-8">[ Clients & brands we've worked with ]</div>
        <div className="flex flex-wrap gap-x-10 gap-y-3">
          {BRANDS.map((b) => (
            <span key={b} className="font-display uppercase font-[800] tracking-[-0.01em] text-2xl sm:text-3xl text-black">{b}</span>
          ))}
        </div>
      </section>

      {/* [04] WORK WITH US / CONTACT */}
      <section id="contact" className="scroll-mt-24 px-4 md:px-6 pt-40 md:pt-56 pb-10">
        <div className="font-mono uppercase text-[12px] tracking-[0.12em] text-muted2 mb-6">[ 04 ]</div>
        <h2 className="font-display uppercase font-[800] tracking-[-0.02em] leading-[1] text-3xl sm:text-4xl lg:text-5xl text-black max-w-[90%] md:max-w-[80%]">
          In brand communication, how you look is where it starts.
        </h2>
        <p className="font-serif text-lg md:text-xl leading-[1.7] text-black mt-8 max-w-[640px]">
          Tell us about your brand and what you want to build. We reply to every serious enquiry, no packages and no pricing games.
        </p>
        <div className="flex flex-wrap items-center gap-8 mt-8">
          <a href="mailto:info@qissastudio.co" data-testid="contact-email" className="font-mono uppercase text-[12px] tracking-[0.12em] hover:underline underline-offset-4">[ Work with us ]</a>
          <Link to="/connect" data-testid="contact-booking" className="font-mono uppercase text-[12px] tracking-[0.12em] hover:underline underline-offset-4">[ Book a call ]</Link>
        </div>
      </section>
    </div>
  );
}
